/**
 * Diccionario de conceptos: qué acumula cada regla y a qué concepto de Visma
 * va lo acumulado.
 *
 * Se editan tres cosas, y las tres desde **un solo diálogo**: el código de
 * Visma, su **parámetro** (`parameterId`, lo que la API exige en cada ítem del
 * payload) y si el concepto se liquida por una **fecha manual** en lugar del
 * mes del período. Lo demás —qué se cuenta, sobre qué cuentas y servicios— es
 * la definición del archivo de RRHH, y cambiarla es cambiar la regla de
 * negocio, no un dato.
 *
 * Un diálogo y no tres botones: los tres valores describen el mismo concepto y
 * se miran juntos antes de decidir. Un código mal escrito manda la novedad a
 * otro concepto de la liquidación, un parámetro equivocado la hace rebotar y
 * una fecha manual mal puesta la deja sin liquidar, así que se confirma y se
 * ve de dónde viene cada valor.
 *
 * El parámetro y la fecha manual son del **concepto**, no de la regla:
 * cambiarlos en una fila los cambia para todas las que mandan a ese mismo
 * concepto, y el diálogo lo dice antes de guardar.
 */

import { useCallback, useEffect, useState } from 'react';

import { Loader2, Pencil, RotateCcw } from 'lucide-react';

import { mensajeDe, type ConceptoRegla } from '../../api';
import { Aviso } from '../../components/Aviso';
import { Modal } from '../../components/Modal';
import { EnvoltorioTabla, FilaVacia } from '../../components/Tabla';
import {
  editarConcepto,
  editarParametro,
  editarRangoFecha,
  listarConceptos,
  restablecerConcepto,
  restablecerParametro,
  restablecerRangoFecha,
} from './api';

/** Lo que el diálogo devuelve: los tres valores, ya como quedaron. */
interface ValoresConcepto {
  readonly codigo: string;
  readonly parametro: number | null;
  readonly fechaManual: boolean;
}

/** Cuál de los tres valores se restablece al del archivo. */
type Campo = 'concepto' | 'parametro' | 'fechaManual';

export function PanelConceptos(): React.JSX.Element {
  const [conceptos, setConceptos] = useState<readonly ConceptoRegla[]>([]);
  const [operacion, setOperacion] = useState('');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [editando, setEditando] = useState<ConceptoRegla | null>(null);
  const [parametrosEditables, setParametrosEditables] = useState(true);
  const [rangosEditables, setRangosEditables] = useState(true);

  const cargar = useCallback(async (): Promise<void> => {
    setCargando(true);
    try {
      const diccionario = await listarConceptos();
      setConceptos(diccionario.conceptos);
      setParametrosEditables(diccionario.parametrosEditables);
      setRangosEditables(diccionario.rangosEditables);
      setError('');
    } catch (err) {
      setConceptos([]);
      setError(mensajeDe(err));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function operar(fn: () => Promise<unknown>, exito: string): Promise<void> {
    try {
      await fn();
      setAviso(exito);
      setError('');
      await cargar();
    } catch (err) {
      setError(mensajeDe(err));
      setAviso('');
    }
  }

  /**
   * Guarda los tres valores en una sola pasada.
   *
   * Solo se manda lo que cambió: guardar los tres siempre dejaría anotado como
   * "editado en el portal" algo que nadie tocó, y eso después nadie se anima a
   * restablecer.
   *
   * El código va **primero**: el parámetro y la fecha manual se guardan por
   * concepto, así que si el código cambió tienen que ir contra el nuevo.
   */
  async function guardar(c: ConceptoRegla, v: ValoresConcepto): Promise<void> {
    const cambios: string[] = [];
    if (v.codigo !== c.concepto) cambios.push(`se liquida en el concepto ${v.codigo}`);
    if (v.parametro !== null && v.parametro !== c.parametro) {
      cambios.push(`se impacta con el parámetro ${String(v.parametro)}`);
    }
    if (v.fechaManual !== c.rangoFecha) {
      cambios.push(
        v.fechaManual ? 'pasa a pedir una fecha manual' : 'vuelve a liquidarse por el mes',
      );
    }

    setEditando(null);
    if (cambios.length === 0) return;

    await operar(async () => {
      if (v.codigo !== c.concepto) await editarConcepto(c.id, v.codigo);
      if (v.parametro !== null && v.parametro !== c.parametro) {
        await editarParametro(v.codigo, v.parametro);
      }
      if (v.fechaManual !== c.rangoFecha) await editarRangoFecha(v.codigo, v.fechaManual);
    }, `${c.nombre}: ${cambios.join(', ')}.`);
  }

  /** Vuelve uno de los tres valores a lo que dice el archivo. */
  async function restablecer(c: ConceptoRegla, campo: Campo): Promise<void> {
    setEditando(null);
    if (campo === 'concepto') {
      await operar(
        () => restablecerConcepto(c.id),
        `${c.nombre}: volvió al código del archivo.`,
      );
      return;
    }
    if (campo === 'parametro') {
      await operar(
        () => restablecerParametro(c.concepto),
        `El concepto ${c.concepto} volvió al parámetro del archivo.`,
      );
      return;
    }
    await operar(
      () => restablecerRangoFecha(c.concepto),
      `El concepto ${c.concepto} vuelve a lo que dice el archivo sobre la fecha manual.`,
    );
  }

  const operaciones = [...new Set(conceptos.map((c) => c.operacion))].sort();
  const visibles = operacion === '' ? conceptos : conceptos.filter((c) => c.operacion === operacion);
  const editados = conceptos.filter(
    (c) => c.editado || c.parametroEditado || c.rangoFechaEditado,
  ).length;

  return (
    <section className="tarjeta">
      <h2>Diccionario de conceptos</h2>
      <p className="nota">
        Cada regla acumula algo del TMS y lo manda a un concepto de Visma. Acá se corrigen el
        código del concepto, su parámetro y si pide una fecha manual; qué se acumula viene del
        archivo de RRHH.
      </p>

      {!parametrosEditables && (
        <Aviso tono="atencion">
          Los parámetros se muestran como los trae el archivo: la tabla que guarda los editados
          todavía no existe en la base. Hasta que se cree, no se pueden cambiar desde acá.
        </Aviso>
      )}
      {!rangosEditables && (
        <Aviso tono="atencion">
          Los conceptos que piden una fecha manual se muestran como los trae el archivo de reglas:
          falta crear en la base la tabla que guarda lo que se marque acá.
        </Aviso>
      )}
      {error !== '' && <Aviso tono="error">{error}</Aviso>}
      {aviso !== '' && <Aviso tono="ok">{aviso}</Aviso>}

      <div className="banda">
        <label className="campo">
          Operación
          <select value={operacion} onChange={(ev) => setOperacion(ev.target.value)}>
            <option value="">Todas</option>
            {operaciones.map((o) => (
              <option key={o} value={o}>
                {o.toUpperCase()}
              </option>
            ))}
          </select>
        </label>
      </div>

      <EnvoltorioTabla
        pie={`${String(visibles.length)} concepto(s), de los cuales ${String(editados)} con algo editado desde el portal`}
      >
        <table className="tabla">
          <thead>
            <tr>
              <th scope="col">Operación</th>
              <th scope="col">Nombre</th>
              <th scope="col">Detalle</th>
              <th scope="col" className="num">Concepto Visma</th>
              <th scope="col" className="num">Parámetro</th>
              <th
                scope="col"
                title="El concepto se consulta por un rango de fechas en lugar del mes del período"
              >
                Fecha manual
              </th>
              <th scope="col">Estado</th>
              <th scope="col">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {cargando && (
              <FilaVacia columnas={8}>
                <Loader2 className="girando" size={16} aria-hidden="true" /> Cargando…
              </FilaVacia>
            )}
            {!cargando && visibles.length === 0 && (
              <FilaVacia columnas={8}>No hay conceptos para esa operación.</FilaVacia>
            )}
            {!cargando &&
              visibles.map((c) => (
                <tr key={c.id}>
                  <td>{c.operacion.toUpperCase()}</td>
                  <td>{c.nombre}</td>
                  <td>{c.detalle}</td>
                  <td className="num codigo">{c.concepto}</td>
                  <td className="num">
                    {c.parametro === null ? (
                      <span
                        className="sin-parametro"
                        title="Sin parámetro no se puede impactar este concepto en Visma"
                      >
                        falta
                      </span>
                    ) : (
                      c.parametro
                    )}
                  </td>
                  {/* Dato, no control: lo que se edita está en el diálogo. */}
                  <td>
                    {c.rangoFecha ? (
                      <span className="chip" title="Se consulta por un rango de fechas propio">
                        Sí
                      </span>
                    ) : (
                      <span className="tenue">—</span>
                    )}
                  </td>
                  <td>
                    {c.activo ? (
                      c.editado || c.parametroEditado || c.rangoFechaEditado ? (
                        'Editado en el portal'
                      ) : (
                        'Según el archivo'
                      )
                    ) : (
                      <span title="La regla no se calcula hasta que RRHH defina cómo se cuenta">
                        Sin definir
                      </span>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="plano"
                      onClick={() => setEditando(c)}
                      aria-label={`Editar ${c.nombre}`}
                    >
                      <Pencil size={15} aria-hidden="true" />
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </EnvoltorioTabla>

      {editando && (
        <DialogoConcepto
          concepto={editando}
          // Cuántas reglas comparten este concepto: el parámetro y la fecha
          // manual las alcanzan a todas, y quien edita tiene que saberlo.
          reglas={conceptos.filter((o) => o.concepto === editando.concepto).length}
          parametrosEditables={parametrosEditables}
          rangosEditables={rangosEditables}
          onCerrar={() => setEditando(null)}
          onGuardar={(valores) => void guardar(editando, valores)}
          onRestablecer={(campo) => void restablecer(editando, campo)}
        />
      )}
    </section>
  );
}

/** El único diálogo de edición: los tres valores que el portal puede cambiar. */
function DialogoConcepto({
  concepto,
  reglas,
  parametrosEditables,
  rangosEditables,
  onCerrar,
  onGuardar,
  onRestablecer,
}: {
  readonly concepto: ConceptoRegla;
  readonly reglas: number;
  readonly parametrosEditables: boolean;
  readonly rangosEditables: boolean;
  readonly onCerrar: () => void;
  readonly onGuardar: (valores: ValoresConcepto) => void;
  readonly onRestablecer: (campo: Campo) => void;
}): React.JSX.Element {
  const [codigo, setCodigo] = useState(concepto.concepto);
  const [parametro, setParametro] = useState(
    concepto.parametro === null ? '' : String(concepto.parametro),
  );
  const [fechaManual, setFechaManual] = useState(concepto.rangoFecha);

  return (
    <Modal
      abierto
      titulo="Concepto de Visma"
      identidad={`${concepto.operacion.toUpperCase()} · ${concepto.nombre}`}
      onCerrar={onCerrar}
      pie={
        <>
          <button type="button" className="plano" onClick={onCerrar}>
            Cancelar
          </button>
          <button type="submit" form="edicion-concepto" className="primaria">
            <Pencil size={15} aria-hidden="true" />
            Guardar
          </button>
        </>
      }
    >
      <form
        id="edicion-concepto"
        className="formulario"
        onSubmit={(ev) => {
          ev.preventDefault();
          onGuardar({
            codigo: codigo.trim(),
            parametro: parametro.trim() === '' ? null : Number(parametro),
            fechaManual,
          });
        }}
      >
        <p>{concepto.detalle}</p>

        <label className="campo">
          <span className="campo-etiqueta">
            Código del concepto
            {concepto.editado && (
              <BotonArchivo campo="concepto" onRestablecer={onRestablecer} />
            )}
          </span>
          <input
            type="text"
            required
            maxLength={20}
            value={codigo}
            onChange={(ev) => setCodigo(ev.target.value)}
          />
        </label>

        <label className="campo">
          <span className="campo-etiqueta">
            Parámetro (parameterId)
            {concepto.parametroEditado && (
              <BotonArchivo campo="parametro" onRestablecer={onRestablecer} />
            )}
          </span>
          <input
            type="number"
            min={1}
            step={1}
            required
            disabled={!parametrosEditables}
            value={parametro}
            onChange={(ev) => setParametro(ev.target.value)}
          />
        </label>

        {/*
          Si el concepto se liquida por una fecha que se escribe al consultar
          en lugar de por el mes del período. Es el caso de la preventa y el
          supermercado: su ciclo no coincide con el mes.
        */}
        <label className="campo campo--casilla">
          <input
            type="checkbox"
            checked={fechaManual}
            disabled={!rangosEditables}
            onChange={(ev) => setFechaManual(ev.target.checked)}
          />
          <span>
            Se liquida por una fecha manual
            <span className="sub">
              No usa el mes del período: quien liquida carga Desde → Hasta en la pantalla de
              novedades. Sin esa fecha el concepto no trae datos.
            </span>
          </span>
          {concepto.rangoFechaEditado && (
            <BotonArchivo campo="fechaManual" onRestablecer={onRestablecer} />
          )}
        </label>

        {reglas > 1 && (
          <Aviso tono="atencion">
            {`El parámetro y la fecha manual son del concepto, no de la regla: esos dos cambios alcanzan a las ${String(reglas)} reglas que liquidan en el ${concepto.concepto}.`}
          </Aviso>
        )}
        <Aviso tono="atencion">
          Los ceros a la izquierda son parte del código: 01178 no es lo mismo que 1178. El
          parámetro es el número que Visma exige en cada novedad, y si está mal la novedad rebota
          o entra en el lugar equivocado de la liquidación.
        </Aviso>
      </form>
    </Modal>
  );
}

/** Vuelve un valor al del archivo. Solo aparece si ese valor está editado. */
function BotonArchivo({
  campo,
  onRestablecer,
}: {
  readonly campo: Campo;
  readonly onRestablecer: (campo: Campo) => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className="enlace"
      title="Volver a lo que dice el archivo de RRHH"
      onClick={() => onRestablecer(campo)}
    >
      <RotateCcw size={13} aria-hidden="true" />
      Volver al archivo
    </button>
  );
}
